import { useState, useEffect, useCallback } from 'react';
import { Comment, VoteType } from '../types/discussion';
import { useAuth } from './useAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface UseDiscussionCommentsParams {
  stockSymbol: string | null;
  category?: string;
  gameDate?: string;
  enabled?: boolean;
}

interface UseDiscussionCommentsReturn {
  comments: Comment[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  postComment: (commentText: string, parentCommentId?: string) => Promise<boolean>;
  voteComment: (commentId: string, voteType: VoteType) => Promise<boolean>;
  removeVote: (commentId: string) => Promise<boolean>;
  updateComment: (commentId: string, commentText: string) => Promise<boolean>;
  deleteComment: (commentId: string) => Promise<boolean>;
  refetch: () => void;
}

/**
 * Hook for managing discussion comments
 * Handles fetching, posting, voting, and optimistic updates
 */
export const useDiscussionComments = ({
  stockSymbol,
  category,
  gameDate,
  enabled = true
}: UseDiscussionCommentsParams): UseDiscussionCommentsReturn => {
  const { session } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch comments from API
   */
  const fetchComments = useCallback(async () => {
    if (!enabled || !stockSymbol || !category) return;

    setIsLoading(true);
    setError(null);

    try {
      const date = gameDate || new Date().toISOString().split('T')[0];
      const headers: HeadersInit = {};

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/stocks/${stockSymbol}/discussion?category=${category}&date=${date}`,
        { headers }
      );

      const result = await response.json();

      if (result.success) {
        setComments(result.data.comments);
        setTotalCount(result.data.totalCount);
      } else {
        setError(result.error?.message || 'Failed to load comments');
      }
    } catch (err) {
      setError('Failed to load comments. Please try again.');
      console.error('Error fetching comments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, stockSymbol, category, gameDate, session]);

  /**
   * Post a new comment or reply
   */
  const postComment = async (
    commentText: string,
    parentCommentId?: string
  ): Promise<boolean> => {
    if (!stockSymbol || !category || !session?.access_token) {
      setError('You must be logged in to comment');
      return false;
    }

    try {
      const date = gameDate || new Date().toISOString().split('T')[0];
      const response = await fetch(
        `${API_BASE_URL}/api/stocks/${stockSymbol}/discussion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            commentText,
            category,
            gameDate: date,
            parentCommentId
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        // Refresh comments to show the new one
        await fetchComments();
        return true;
      } else {
        setError(result.error?.message || 'Failed to post comment');
        return false;
      }
    } catch (err) {
      setError('Failed to post comment. Please try again.');
      console.error('Error posting comment:', err);
      return false;
    }
  };

  /**
   * Vote on a comment (optimistic update)
   */
  const voteComment = async (
    commentId: string,
    voteType: VoteType
  ): Promise<boolean> => {
    if (!session?.access_token) {
      setError('You must be logged in to vote');
      return false;
    }

    // Optimistic update
    const updateVoteInTree = (comments: Comment[]): Comment[] => {
      return comments.map(comment => {
        if (comment.id === commentId) {
          const wasUpvoted = comment.currentUserVote === 'up';
          const wasDownvoted = comment.currentUserVote === 'down';

          let scoreChange = 0;
          if (voteType === 'up') {
            if (wasUpvoted) {
              // Clicking up again = remove upvote
              scoreChange = -1;
            } else if (wasDownvoted) {
              // Changing from down to up
              scoreChange = 2;
            } else {
              // New upvote
              scoreChange = 1;
            }
          } else {
            if (wasDownvoted) {
              // Clicking down again = remove downvote
              scoreChange = 1;
            } else if (wasUpvoted) {
              // Changing from up to down
              scoreChange = -2;
            } else {
              // New downvote
              scoreChange = -1;
            }
          }

          return {
            ...comment,
            voteScore: comment.voteScore + scoreChange,
            currentUserVote: comment.currentUserVote === voteType ? null : voteType
          };
        }

        if (comment.replies) {
          return {
            ...comment,
            replies: updateVoteInTree(comment.replies)
          };
        }

        return comment;
      });
    };

    setComments(prev => updateVoteInTree(prev));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/comments/${commentId}/vote`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ voteType })
        }
      );

      const result = await response.json();

      if (!result.success) {
        // Revert optimistic update on failure
        await fetchComments();
        setError(result.error?.message || 'Failed to vote');
        return false;
      }

      return true;
    } catch (err) {
      // Revert optimistic update on error
      await fetchComments();
      setError('Failed to vote. Please try again.');
      console.error('Error voting on comment:', err);
      return false;
    }
  };

  /**
   * Remove vote from a comment
   */
  const removeVote = async (commentId: string): Promise<boolean> => {
    if (!session?.access_token) return false;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/comments/${commentId}/vote`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        await fetchComments();
        return true;
      } else {
        setError(result.error?.message || 'Failed to remove vote');
        return false;
      }
    } catch (err) {
      setError('Failed to remove vote. Please try again.');
      console.error('Error removing vote:', err);
      return false;
    }
  };

  /**
   * Update a comment's text
   */
  const updateComment = async (
    commentId: string,
    commentText: string
  ): Promise<boolean> => {
    if (!session?.access_token) return false;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/comments/${commentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ commentText })
        }
      );

      const result = await response.json();

      if (result.success) {
        await fetchComments();
        return true;
      } else {
        setError(result.error?.message || 'Failed to update comment');
        return false;
      }
    } catch (err) {
      setError('Failed to update comment. Please try again.');
      console.error('Error updating comment:', err);
      return false;
    }
  };

  /**
   * Delete a comment (soft delete)
   */
  const deleteComment = async (commentId: string): Promise<boolean> => {
    if (!session?.access_token) return false;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        await fetchComments();
        return true;
      } else {
        setError(result.error?.message || 'Failed to delete comment');
        return false;
      }
    } catch (err) {
      setError('Failed to delete comment. Please try again.');
      console.error('Error deleting comment:', err);
      return false;
    }
  };

  // Fetch comments on mount and when dependencies change
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return {
    comments,
    totalCount,
    isLoading,
    error,
    postComment,
    voteComment,
    removeVote,
    updateComment,
    deleteComment,
    refetch: fetchComments
  };
};
